import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert, AlertTitle, Box, Button, Card, CardContent, Chip, CircularProgress,
  Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Divider, FormControl, FormControlLabel, Grid, IconButton, Link, MenuItem, Paper,
  Radio, RadioGroup, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material'
import {
  ArrowBack, AutoFixHigh, CheckCircle, DeleteOutline, Description, ExpandLess,
  ExpandMore, HelpOutline, History, InfoOutlined, Lock, Refresh, UploadFile, Warning,
} from '@mui/icons-material'

import {
  byofApi, ByofControl, ByofControlPreview, ByofFramework, ByofPreview,
  ByofProposal, ByofVersion, EnforcedBy,
} from '../services/api'
import useCanContactVendor from '../hooks/useCanContactVendor'

/**
 * A tenant's own compliance frameworks: create one, add documents and rules to
 * it, and freeze it when it is worth standing behind.
 *
 * Lives inside Custom Rules rather than on a page of its own because all three
 * sources of a tenant rule — uploaded artifacts, the rule form, templates —
 * now belong together. Two of them were already here.
 *
 * The review screen keeps its full-screen treatment inside this panel. Its
 * enforcement banner, review queue and unparsed list are the honesty surfaces
 * of the whole feature, and a denser layout buries exactly those.
 */

const ACCEPTED = '.yaml,.yml,.json,.csv,.feature,.md,.txt'

const ENFORCEMENT_LABELS: Record<EnforcedBy, string> = {
  conditions: 'Checked',
  pattern: 'Checked (pattern)',
  none: 'Not checked',
}

const ENFORCEMENT_COLORS: Record<EnforcedBy, 'success' | 'default'> = {
  conditions: 'success',
  pattern: 'success',
  none: 'default',
}

const CONFIDENCE_COLORS: Record<string, 'success' | 'warning' | 'default'> = {
  high: 'success',
  medium: 'warning',
  low: 'default',
}

const FORMAT_LABELS: Record<string, string> = {
  gherkin: 'Gherkin feature file',
  gherkin_in_yaml: 'Gherkin inside YAML',
  oscal: 'OSCAL catalog',
  rules_yaml: 'Rules in YAML',
  controls_tree: 'Nested controls',
  tabular: 'Table (CSV)',
  unknown: 'Not recognised',
}

const SOURCE_LABELS: Record<string, string> = {
  document: 'From a document you uploaded',
  rule: 'From a rule you wrote — the rule is the obligation',
  manual: 'Added directly',
}

const formatName = (f: string) => FORMAT_LABELS[f] || f

interface Props {
  /** Surfaced by the parent so the Rules tab can refresh its framework chips. */
  onFrameworksChanged?: () => void
}

const FrameworksPanel = ({ onFrameworksChanged }: Props) => {
  const [loading, setLoading] = useState(true)
  const [frameworks, setFrameworks] = useState<ByofFramework[]>([])
  const [error, setError] = useState<string | null>(null)
  const [notEntitled, setNotEntitled] = useState(false)
  const [noTenant, setNoTenant] = useState(false)
  const canContactVendor = useCanContactVendor()

  const [selected, setSelected] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const fileInput = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [runId, setRunId] = useState<string | null>(null)
  const [preview, setPreview] = useState<ByofPreview | null>(null)
  const [unreadable, setUnreadable] = useState<{ format: string; errors: string[] } | null>(null)
  const [approving, setApproving] = useState(false)
  const [approved, setApproved] = useState<string | null>(null)
  const [showUnparsed, setShowUnparsed] = useState(false)
  // Category corrections the reviewer made, sent with the approval. Held here
  // rather than in the review screen so discarding a preview drops them too —
  // a correction to a document you did not approve should not survive.
  const [overrides, setOverrides] = useState<Record<string, string[]>>({})
  const [confirmDelete, setConfirmDelete] = useState<ByofFramework | null>(null)

  const fetchFrameworks = useCallback(async () => {
    setLoading(true)
    setError(null)
    setNotEntitled(false)
    setNoTenant(false)
    try {
      const resp = await byofApi.list()
      setFrameworks(resp.data.frameworks || [])
    } catch (e: any) {
      const status = e?.response?.status
      // 404 rather than 403 on purpose: a tenant without the feature should
      // not learn it exists from the status code.
      if (status === 404) setNotEntitled(true)
      else if (status === 400) setNoTenant(true)
      else setError('Unable to load your frameworks. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchFrameworks() }, [fetchFrameworks])

  const selectedFramework = useMemo(
    () => frameworks.find((f) => f.key === selected) || null,
    [frameworks, selected],
  )

  const onFileChosen = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || !selected) return
    setUploading(true)
    setError(null)
    setPreview(null)
    setUnreadable(null)
    setApproved(null)
    try {
      const resp = await byofApi.uploadDocument(selected, file)
      setRunId(resp.data.run_id)
      if (resp.data.readable && resp.data.preview) setPreview(resp.data.preview)
      else setUnreadable({
        format: resp.data.detection?.format || 'unknown',
        errors: resp.data.errors || [],
      })
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const discardReview = () => {
    setRunId(null)
    setPreview(null)
    setUnreadable(null)
    setOverrides({})
  }

  const approve = async () => {
    if (!runId || !selected) return
    setApproving(true)
    setError(null)
    try {
      const resp = await byofApi.approveDocument(selected, runId, {
        category_overrides: Object.keys(overrides).length ? overrides : undefined,
      })
      setApproved(resp.data.enforcement)
      discardReview()
      await fetchFrameworks()
      onFrameworksChanged?.()
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not commit this document.')
    } finally {
      setApproving(false)
    }
  }

  const remove = async () => {
    if (!confirmDelete) return
    const target = confirmDelete
    setConfirmDelete(null)
    try {
      const resp = await byofApi.remove(target.key)
      if (selected === target.key) setSelected(null)
      await fetchFrameworks()
      onFrameworksChanged?.()
      const kept = resp.data.rules_kept?.length || 0
      if (kept) {
        setApproved(
          `${target.display_name} was removed. ${kept} rule(s) were kept — they are ` +
          `used by another framework, or you wrote or edited them yourself.`,
        )
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not remove this framework.')
    }
  }

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
  }

  if (noTenant) {
    return (
      <Alert severity="info">
        <AlertTitle>This session has no tenant</AlertTitle>
        A compliance framework belongs to a tenant, and a platform admin account has
        none of its own. Sign in as a user of the tenant you are working on.
      </Alert>
    )
  }

  if (notEntitled) {
    return (
      <Alert severity="info">
        <AlertTitle>Not enabled for your account</AlertTitle>
        Bringing your own compliance framework is an Enterprise capability. Upload your
        controls, have them mapped to what ARIAS detects, and track them alongside the
        published frameworks.{' '}
        {canContactVendor ? (
          <>
            Contact <Link href="mailto:sales@tryarias.com">sales@tryarias.com</Link> to
            enable it.
          </>
        ) : (
          'Ask your tenant admin to enable it.'
        )}
      </Alert>
    )
  }

  return (
    <Box>
      <input ref={fileInput} type="file" accept={ACCEPTED} onChange={onFileChosen} style={{ display: 'none' }} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {approved && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setApproved(null)}>{approved}</Alert>
      )}
      {uploading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Reading your file — detecting its format, then its controls.
          </Typography>
        </Box>
      )}

      {preview && selected ? (
        <ReviewScreen
          preview={preview}
          approving={approving}
          onApprove={approve}
          onDiscard={discardReview}
          showUnparsed={showUnparsed}
          onToggleUnparsed={() => setShowUnparsed((v) => !v)}
          overrides={overrides}
          onOverride={(controlId, categories) =>
            setOverrides((prev) => ({ ...prev, [controlId]: categories }))}
        />
      ) : unreadable ? (
        <UnreadableNotice {...unreadable} onDismiss={discardReview} />
      ) : selectedFramework ? (
        <FrameworkDetail
          framework={selectedFramework}
          onBack={() => setSelected(null)}
          onAddDocument={() => fileInput.current?.click()}
          onChanged={async () => { await fetchFrameworks(); onFrameworksChanged?.() }}
          onError={setError}
        />
      ) : (
        <FrameworkList
          frameworks={frameworks}
          onOpen={setSelected}
          onDelete={setConfirmDelete}
          onCreate={() => setCreating(true)}
          onRefresh={fetchFrameworks}
        />
      )}

      <CreateFrameworkDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={async (key) => {
          setCreating(false)
          await fetchFrameworks()
          onFrameworksChanged?.()
          setSelected(key)
        }}
        onError={setError}
      />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Remove {confirmDelete?.display_name}?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Its {confirmDelete?.controls} control{confirmDelete?.controls === 1 ? '' : 's'} go,
            and it stops producing findings.
            <Box sx={{ mt: 1.5 }}>
              Rules used by another framework are kept, and so are rules you wrote or
              edited yourself — they stay on your rules list.
            </Box>
            <Box sx={{ mt: 1.5 }}>
              The files you uploaded and the record of each ingest are kept. Those are
              the evidence that this framework existed and what it said, which is
              exactly what an auditor asks about a control you have withdrawn.
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button color="error" onClick={remove}>Remove</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// --- create -----------------------------------------------------------------

/**
 * Evidence scope is chosen here, at creation, and explained in full.
 *
 * It is the one control the author has over how every number they see later is
 * computed, so it cannot be a tooltip on a settings screen they never open.
 */
const CreateFrameworkDialog = ({
  open, onClose, onCreated, onError,
}: {
  open: boolean
  onClose: () => void
  onCreated: (key: string) => void
  onError: (message: string) => void
}) => {
  const [displayName, setDisplayName] = useState('')
  const [version, setVersion] = useState('')
  const [scope, setScope] = useState<'augmented' | 'exclusive'>('augmented')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    setSaving(true)
    try {
      const resp = await byofApi.create({
        display_name: displayName,
        version,
        evidence_scope: scope,
      })
      setDisplayName('')
      setVersion('')
      setScope('augmented')
      onCreated(resp.data.key)
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Could not create this framework.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New framework</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus fullWidth margin="dense" label="Name"
          placeholder="e.g. Group AI Assurance Standard"
          value={displayName} onChange={(e) => setDisplayName(e.target.value)}
        />
        <TextField
          fullWidth margin="dense" label="Version (optional)" placeholder="e.g. 2.1"
          value={version} onChange={(e) => setVersion(e.target.value)}
        />
        <Typography variant="subtitle2" sx={{ mt: 2 }}>How should it be judged?</Typography>
        <RadioGroup value={scope} onChange={(e) => setScope(e.target.value as any)}>
          <FormControlLabel
            value="augmented" control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2">Its own checks, plus everything ARIAS finds</Typography>
                <Typography variant="caption" color="text.secondary">
                  Each control also draws on findings in its category, from every agent
                  ARIAS has scanned — so a control lights up before you have written a
                  single rule. Most customers want this.
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="exclusive" control={<Radio size="small" />}
            label={
              <Box>
                <Typography variant="body2">Only this framework's own evidence</Typography>
                <Typography variant="caption" color="text.secondary">
                  A narrower view. Findings outside it are still detected and still
                  real — hidden from this framework's totals, not resolved.
                </Typography>
              </Box>
            }
          />
        </RadioGroup>
        <Typography variant="caption" color="text.secondary">
          Changeable at any time afterwards. It is a read-time lens: nothing is
          recomputed and no history is lost.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!displayName.trim() || saving}>
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// --- list -------------------------------------------------------------------

const FrameworkList = ({
  frameworks, onOpen, onDelete, onCreate, onRefresh,
}: {
  frameworks: ByofFramework[]
  onOpen: (key: string) => void
  onDelete: (f: ByofFramework) => void
  onCreate: () => void
  onRefresh: () => void
}) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Your own compliance frameworks, tracked alongside the published ones. Build one
        from uploaded documents, rules you write, or both.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <IconButton onClick={onRefresh} aria-label="Refresh"><Refresh /></IconButton>
        <Button variant="contained" onClick={onCreate}>New framework</Button>
      </Box>
    </Box>

    {!frameworks.length ? (
      <Card variant="outlined">
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Description sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" gutterBottom>No frameworks yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 580, mx: 'auto', mb: 2 }}>
            Create a framework, then add to it: upload your controls as YAML, JSON, CSV
            or a Gherkin feature file, and add rules you write here. We show you what we
            made of each document before anything is saved, and say plainly what we
            could not read.
          </Typography>
          <Button variant="contained" onClick={onCreate}>New framework</Button>
        </CardContent>
      </Card>
    ) : (
      <Grid container spacing={2}>
        {frameworks.map((f) => (
          <Grid item xs={12} md={6} key={f.key}>
            <Card variant="outlined" sx={{ height: '100%', cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
              <CardContent onClick={() => onOpen(f.key)}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h6">{f.display_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {f.version || 'unversioned'} · {f.controls} control{f.controls === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                  <Tooltip title="Remove this framework">
                    <IconButton
                      size="small" aria-label="Remove"
                      onClick={(e) => { e.stopPropagation(); onDelete(f) }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Chip
                  size="small" sx={{ mt: 1.5 }} variant="outlined"
                  label={f.evidence_scope === 'exclusive'
                    ? 'Judged on its own rules only'
                    : 'Judged on its rules plus ARIAS findings'}
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    )}
  </Box>
)

// --- one framework ----------------------------------------------------------

const FrameworkDetail = ({
  framework, onBack, onAddDocument, onChanged, onError,
}: {
  framework: ByofFramework
  onBack: () => void
  onAddDocument: () => void
  onChanged: () => void
  onError: (message: string) => void
}) => {
  const [controls, setControls] = useState<ByofControl[]>([])
  const [counts, setCounts] = useState({ controls: 0, checked: 0, unchecked: 0 })
  const [versions, setVersions] = useState<ByofVersion[]>([])
  const [freezing, setFreezing] = useState(false)
  const [label, setLabel] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [rebuilt, setRebuilt] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [c, v] = await Promise.all([
        byofApi.controls(framework.key),
        byofApi.versions(framework.key),
      ])
      setControls(c.data.controls || [])
      setCounts(c.data.counts)
      setVersions(v.data.versions || [])
    } catch {
      onError('Could not load this framework.')
    }
  }, [framework.key, onError])

  useEffect(() => { load() }, [load])

  const toggleScope = async () => {
    const next = framework.evidence_scope === 'exclusive' ? 'augmented' : 'exclusive'
    try {
      await byofApi.update(framework.key, { evidence_scope: next })
      onChanged()
    } catch {
      onError('Could not change the evidence scope.')
    }
  }

  const rebuild = async () => {
    setBusy(true)
    try {
      const resp = await byofApi.rebuildRules(framework.key)
      setRebuilt(resp.data.statement)
      await load()
      onChanged()
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Could not create rules.')
    } finally {
      setBusy(false)
    }
  }

  const freeze = async () => {
    setBusy(true)
    try {
      await byofApi.freezeVersion(framework.key, { label, note })
      setFreezing(false)
      setLabel('')
      setNote('')
      await load()
    } catch (e: any) {
      onError(e?.response?.data?.detail || 'Could not freeze a version.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Link component="button" onClick={onBack} sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <ArrowBack fontSize="small" sx={{ mr: 0.5 }} /> All frameworks
        </Link>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {framework.display_name} {framework.version}
        </Typography>
        <Button startIcon={<UploadFile />} onClick={onAddDocument}>Add document</Button>
        {counts.controls > 0 && counts.checked === 0 && (
          <Tooltip title="Your controls were added before we had seen a scan, so none of them has a rule testing it yet. This writes those rules from the controls you already approved — no need to upload anything again.">
            <Button variant="outlined" startIcon={<AutoFixHigh />} onClick={rebuild} disabled={busy}>
              Create rules from your controls
            </Button>
          </Tooltip>
        )}
        <Button startIcon={<Lock />} onClick={() => setFreezing(true)} disabled={!counts.controls}>
          Freeze version
        </Button>
      </Box>

      {rebuilt && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRebuilt(null)}>{rebuilt}</Alert>
      )}

      {counts.controls > 0 && counts.checked === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>These controls have no rules yet</AlertTitle>
          They were added before we had seen a scan from you, so we could not know what
          your agents report. They are still collecting evidence by category. Once you
          have run a scan, use <strong>Create rules from your controls</strong> above and
          we will write the rules we can — you do not need to upload anything again.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>Where evidence comes from</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip size="small" color="success" label={`${counts.checked} tested by a rule`} />
                <Chip size="small" label={`${counts.unchecked} category evidence only`} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                A control with <strong>category evidence only</strong> is still part of
                your framework and still appears in your exports. It collects findings
                ARIAS produces in its category; nothing tests its own wording, and it is
                never counted as met.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>How it is judged</Typography>
              <Chip
                size="small" variant="outlined" clickable onClick={toggleScope}
                label={framework.evidence_scope === 'exclusive'
                  ? 'Its own rules only'
                  : 'Its rules plus ARIAS findings'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {framework.evidence_scope === 'exclusive'
                  ? 'Findings outside this framework are still detected — hidden from its totals, not resolved.'
                  : 'Each control also draws on findings in its category, from every agent ARIAS has scanned.'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Controls</Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Control</TableCell>
                  <TableCell>Came from</TableCell>
                  <TableCell align="right">Checked by</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {controls.map((c) => (
                  <TableRow key={c.control_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {c.control_id}
                      </Typography>
                      <Typography variant="body2">{c.control_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={SOURCE_LABELS[c.source] || c.source}>
                        <Chip size="small" variant="outlined" label={c.source_document || c.source} />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {c.checked
                        ? <Chip size="small" color="success" label={`${c.rules.length} rule${c.rules.length === 1 ? '' : 's'}`} />
                        : <Chip size="small" label="nothing" />}
                    </TableCell>
                  </TableRow>
                ))}
                {!controls.length && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        Nothing in this framework yet. Add a document, or add a rule to it
                        from the Rules tab.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <History fontSize="small" />
            <Typography variant="subtitle2">Frozen versions</Typography>
          </Box>
          {versions.length ? (
            versions.map((v) => (
              <Box key={v.id} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2">
                  <strong>v{v.version}</strong>{v.label ? ` · ${v.label}` : ''} —{' '}
                  {new Date(v.created_at).toLocaleDateString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {v.statement}{v.note ? ` · ${v.note}` : ''}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="caption" color="text.secondary">
              None yet. Freeze one when this framework reaches a state you intend to
              stand behind — a certificate issued today can then be explained after
              you edit it.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Dialog open={freezing} onClose={() => setFreezing(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Freeze this framework</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ mb: 2 }}>
            Records all {counts.controls} control{counts.controls === 1 ? '' : 's'} and the
            full definition of every rule that checks them — not just a reference, so
            later edits will not change what this version says.
            <Box sx={{ mt: 1 }}>
              The live framework is untouched. Keep editing afterwards as normal.
            </Box>
          </DialogContentText>
          <TextField
            fullWidth margin="dense" label="Label (optional)" placeholder="e.g. 2.1, as certified"
            value={label} onChange={(e) => setLabel(e.target.value)}
          />
          <TextField
            fullWidth margin="dense" label="Why are you freezing this? (optional)"
            placeholder="e.g. baseline for the Q3 audit"
            value={note} onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFreezing(false)}>Cancel</Button>
          <Button variant="contained" onClick={freeze} disabled={busy}>
            {busy ? 'Freezing…' : 'Freeze'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// --- unreadable -------------------------------------------------------------

const UnreadableNotice = ({
  format, errors, onDismiss,
}: { format: string; errors: string[]; onDismiss: () => void }) => (
  <Card variant="outlined">
    <CardContent>
      <Alert severity="warning">
        <AlertTitle>We recognised the format but could not read it</AlertTitle>
        We read this as <strong>{formatName(format)}</strong>, but could not turn it into
        controls. Nothing was saved.
        {errors.length > 0 && (
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
            {errors.map((e, i) => <li key={i}><Typography variant="body2">{e}</Typography></li>)}
          </Box>
        )}
      </Alert>
      <Box sx={{ mt: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={onDismiss}>Back</Button>
      </Box>
    </CardContent>
  </Card>
)

// --- review -----------------------------------------------------------------

const ReviewScreen = ({
  preview, approving, onApprove, onDiscard, showUnparsed, onToggleUnparsed,
  overrides, onOverride,
}: {
  preview: ByofPreview
  approving: boolean
  onApprove: () => void
  onDiscard: () => void
  showUnparsed: boolean
  onToggleUnparsed: () => void
  overrides: Record<string, string[]>
  onOverride: (controlId: string, categories: string[]) => void
}) => {
  const { enforcement, detection, counts, controls, needs_review: needsReview, diff, unparsed } = preview

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Link component="button" onClick={onDiscard} sx={{ display: 'flex', alignItems: 'center', mr: 2, textDecoration: 'none' }}>
          <ArrowBack fontSize="small" sx={{ mr: 0.5 }} /> Discard
        </Link>
        <Typography variant="h6">Review this document</Typography>
      </Box>

      <EnforcementBanner enforcement={enforcement} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}><DetectionCard detection={detection} counts={counts} /></Grid>
        <Grid item xs={12} md={5}><CoverageCard preview={preview} /></Grid>
      </Grid>

      {preview.notes.filter((n) => n !== enforcement.statement).length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>About this reading</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {preview.notes.filter((n) => n !== enforcement.statement).map((note, i) => (
                <li key={i}><Typography variant="body2" color="text.secondary">{note}</Typography></li>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {diff && <DiffCard diff={diff} />}
      {needsReview.length > 0 && (
        <NeedsReviewCard items={needsReview} overrides={overrides} onOverride={onOverride} />
      )}
      <ControlsPreviewTable controls={controls} />

      {unparsed.length > 0 && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={onToggleUnparsed}>
              <Warning fontSize="small" color="warning" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                {unparsed.length} item{unparsed.length === 1 ? '' : 's'} we could not read
              </Typography>
              {showUnparsed ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Nothing was discarded. Each is listed with the reason so you can decide
              whether it matters.
            </Typography>
            <Collapse in={showUnparsed}>
              <Box component="ul" sx={{ mt: 1.5, mb: 0, pl: 2.5 }}>
                {unparsed.map((item, i) => (
                  <li key={i}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {String(item.reason || JSON.stringify(item))}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mt: 2, position: 'sticky', bottom: 0, zIndex: 2 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Approving adds this document's controls to the framework. Other documents
            are unaffected; re-approving this same file replaces only what it
            contributed.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onDiscard} disabled={approving}>Discard</Button>
            <Button variant="contained" onClick={onApprove} disabled={approving}>
              {approving ? 'Adding…' : `Add ${counts.controls} control${counts.controls === 1 ? '' : 's'}`}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}

const EnforcementBanner = ({ enforcement }: { enforcement: ByofPreview['enforcement'] }) => {
  const { assessable, statement, unenforced = 0, rules = 0 } = enforcement
  const severity = !assessable ? 'info' : unenforced > 0 ? 'warning' : 'success'
  return (
    <Alert severity={severity} icon={assessable && unenforced === 0 ? <CheckCircle /> : undefined} sx={{ mb: 2 }}>
      <AlertTitle>
        {assessable ? 'Where each control will get its evidence' : 'Your controls are ready — rules come after your first scan'}
      </AlertTitle>
      {!assessable ? (
        <>
          <Typography variant="body2">
            We will record every control in this document and route findings to it by
            category, so this framework starts working straight away.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            What we cannot do yet is write rules that test your controls directly. A
            rule has to compare something an agent reports — its guardrails, its
            autonomy, the tools it declares — and we learn what your agents report from
            your scans. Once you have run one, this framework will offer to create
            those rules; you do not need to upload anything again.
          </Typography>
        </>
      ) : (
        <Typography variant="body2">{statement}</Typography>
      )}
      {assessable && unenforced > 0 && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Unchecked controls are still part of your framework and still appear in your
          exports. They draw evidence from findings in their category — they are not
          verified directly, and are never counted as met.
        </Typography>
      )}
      {assessable && (enforcement.skipped_no_category?.length ?? 0) > 0 && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {enforcement.skipped_no_category!.length} control(s) could not be matched to any
          ARIAS category, so no check was built and they will draw no evidence:{' '}
          <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {enforcement.skipped_no_category!.slice(0, 12).join(', ')}
          </Box>
        </Typography>
      )}
      {assessable && rules > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          A rule can only compare something your agents actually report — their
          guardrails, their autonomy, the tools they declare. We read that from your
          recent scans, so a control about something your agents do not report cannot
          have a rule, however it is worded.
        </Typography>
      )}
    </Alert>
  )
}

const Stat = ({ label, value }: { label: string; value: number }) => (
  <Grid item xs={3}>
    <Typography variant="h6">{value}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Grid>
)

const DetectionCard = ({
  detection, counts,
}: { detection: ByofPreview['detection']; counts: ByofPreview['counts'] }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle2" gutterBottom>What we read</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
        <Chip size="small" label={formatName(detection.format)} color="primary" variant="outlined" />
        <Typography variant="caption" color="text.secondary">
          confidence {Math.round((detection.confidence ?? 0) * 100)}%
        </Typography>
      </Box>
      {detection.reconsidered_from && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          First read as {formatName(detection.reconsidered_from)}, then re-read after
          learning your document's own keywords.
        </Typography>
      )}
      {(detection.evidence || []).length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {detection.evidence.map((e, i) => (
            <li key={i}><Typography variant="caption" color="text.secondary">{e}</Typography></li>
          ))}
        </Box>
      )}
      <Divider sx={{ my: 1.5 }} />
      <Grid container spacing={1}>
        <Stat label="Controls" value={counts.controls} />
        <Stat label="Assertions" value={counts.assertions} />
        <Stat label="Bound" value={counts.bound} />
        <Stat label="Unbound" value={counts.unbound} />
      </Grid>
    </CardContent>
  </Card>
)

const CoverageCard = ({ preview }: { preview: ByofPreview }) => {
  const { coverage } = preview
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>Evidence you already have</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          These controls draw on findings ARIAS has already produced, before you write a
          single rule of your own.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip size="small" color="success" label={`${coverage.covered} with findings`} />
          <Chip size="small" label={`${coverage.no_evidence_yet} awaiting evidence`} />
          <Chip size="small" color="warning" label={`${coverage.not_bound} not bound`} />
        </Box>
        <Typography variant="body2">
          <strong>{coverage.percent_of_bound_controls_covered}%</strong> of {coverage.denominator}
        </Typography>
        <Tooltip title="Stated over bound controls only. A percentage over all controls would move whenever we failed to read part of your file, making it a measure of our parser rather than of your posture.">
          <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
            Why this denominator <InfoOutlined sx={{ fontSize: 12 }} />
          </Typography>
        </Tooltip>
      </CardContent>
    </Card>
  )
}

const DiffCard = ({ diff }: { diff: NonNullable<ByofPreview['diff']> }) => (
  <Card variant="outlined" sx={{ mb: 2 }}>
    <CardContent>
      <Typography variant="subtitle2" gutterBottom>
        What changed since you last uploaded this file
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Chip size="small" color="success" label={`${diff.added.length} added`} />
        <Chip size="small" color="error" label={`${diff.removed.length} removed`} />
        <Chip size="small" color="warning" label={`${diff.changed.length} changed`} />
        <Chip size="small" label={`${diff.unchanged} unchanged`} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Compared against this document only. Controls from your framework's other
        documents are untouched.
        {diff.removed.length > 0 && (
          <> Removed on approval:{' '}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>{diff.removed.slice(0, 12).join(', ')}</Box>
          </>
        )}
      </Typography>
    </CardContent>
  </Card>
)

/** The 27 ARIAS categories, for a reviewer correcting a binding by hand. */
const ARIAS_CATEGORIES = [
  'prompt_injection_defense', 'system_prompt_construction', 'input_validation',
  'output_format_parsing', 'refusal_scope', 'tool_governance', 'error_handling',
  'model_configuration', 'autonomy_controls', 'code_execution_safety',
  'context_window_management', 'cross_session_isolation', 'sensitive_state_handling',
  'multi_agent_coordination', 'state_management', 'subagent_spawning',
  'inter_agent_authentication', 'tracing_callbacks', 'audit_trail', 'logging_coverage',
  'metrics_cost_tracking', 'rate_limiting_quotas', 'human_in_the_loop',
  'policy_alignment', 'regulatory_compliance', 'secrets_credentials', 'access_control',
]

/**
 * What we were unsure about — split by what it actually is, and actionable.
 *
 * These were one undifferentiated table titled "22 things we were not sure
 * about", which is alarming and unhelpful in the same breath: it mixes "we
 * read `designation` as the framework name" — obviously right, visible in the
 * title above — with "we are not certain AAS-06 belongs in policy_alignment",
 * which changes what evidence the control collects. And nothing could be
 * acted on, so it was a warning label rather than a review.
 *
 * Category bindings come first and are editable, because they are the ones
 * that change the outcome. How we read the document is second and read-only:
 * if it were wrong the controls above would obviously be wrong, which is a
 * better check than reading a list of field names.
 */
const NeedsReviewCard = ({
  items, overrides, onOverride,
}: {
  items: ByofProposal[]
  overrides: Record<string, string[]>
  onOverride: (controlId: string, categories: string[]) => void
}) => {
  const bindings = items.filter((i) => i.target.startsWith('category.'))
  const reading = items.filter((i) => !i.target.startsWith('category.'))

  return (
    <Card variant="outlined" sx={{ mb: 2, borderColor: 'warning.main' }}>
      <CardContent>
        <Typography variant="subtitle2" gutterBottom>Worth a look before you approve</Typography>

        {bindings.length > 0 && (
          <Box sx={{ mb: reading.length ? 3 : 0 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {bindings.length} control{bindings.length === 1 ? '' : 's'} we were not
              confident about placing
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              A control's category decides which findings it collects, so this is the one
              worth correcting. Change it here and your choice is recorded as yours.
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Control</TableCell>
                    <TableCell>We chose</TableCell>
                    <TableCell>Because</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bindings.map((item, i) => {
                    const controlId = item.target.replace(/^category\./, '')
                    const current = overrides[controlId] ?? [String(item.value)]
                    return (
                      <TableRow key={`${item.target}-${i}`}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {controlId}
                        </TableCell>
                        <TableCell sx={{ minWidth: 240 }}>
                          <FormControl size="small" fullWidth>
                            <Select
                              multiple
                              value={current}
                              onChange={(e) => onOverride(
                                controlId,
                                typeof e.target.value === 'string'
                                  ? e.target.value.split(',')
                                  : (e.target.value as string[]),
                              )}
                              renderValue={(selected) => (selected as string[]).join(', ')}
                            >
                              {ARIAS_CATEGORIES.map((c) => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {overrides[controlId] && (
                            <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 0.5 }}>
                              your choice — will be recorded as yours
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {(item.evidence || []).join(' · ')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {reading.length > 0 && (
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              How we read your document ({reading.length} judgement{reading.length === 1 ? '' : 's'})
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Listed so nothing is hidden, but you do not need to check them line by
              line: if we had read your document wrongly, the controls below would be
              visibly wrong — which is a better test than this list.
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>What</TableCell><TableCell>We read it as</TableCell>
                    <TableCell>Because</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reading.map((item, i) => (
                    <TableRow key={`${item.target}-${i}`}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{item.target}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {Array.isArray(item.value) ? item.value.join(', ') : String(item.value)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {(item.evidence || []).join(' · ')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

const ControlsPreviewTable = ({ controls }: { controls: ByofControlPreview[] }) => {
  const [onlyUnchecked, setOnlyUnchecked] = useState(false)
  const shown = useMemo(
    () => (onlyUnchecked ? controls.filter((c) => c.enforced_by === 'none' || c.enforced_by === null) : controls),
    [controls, onlyUnchecked],
  )

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">Controls in this document</Typography>
          <Button size="small" onClick={() => setOnlyUnchecked((v) => !v)}>
            {onlyUnchecked ? `Show all ${controls.length}` : 'Show only category-evidence controls'}
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Every control draws evidence one of two ways. A <strong>rule</strong> tests the
          control's own wording against what your agents report, and the test is shown
          in words. <strong>Category only</strong> means no rule tests it directly — it
          collects findings ARIAS already produces in its category, which is most of a
          framework and is not a failure.
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Control</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Evidence</TableCell>
                <TableCell align="right">Findings today</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((control) => <ControlRow key={control.source_id} control={control} />)}
              {!shown.length && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Every control has a rule testing it directly.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}

const ControlRow = ({ control }: { control: ByofControlPreview }) => {
  const enforced = control.enforced_by
  const categoryNames = control.categories.map((c) => c.category)

  return (
    <TableRow hover>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
          {control.source_id}
        </Typography>
        <Typography variant="body2">{control.title}</Typography>
        {control.severity && (
          <Tooltip title="Your framework's own grading, mapped to an ARIAS severity.">
            <Chip size="small" label={control.severity} variant="outlined" sx={{ mt: 0.5, mr: 0.5 }} />
          </Tooltip>
        )}
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top' }}>
        {control.categories.length === 0 ? (
          <Tooltip title="We could not match this control to anything ARIAS detects, so it will collect no evidence at all. Bind it by hand below, or leave it recorded.">
            <Chip size="small" label="none" color="warning" variant="outlined" />
          </Tooltip>
        ) : (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {control.categories.map((c) => (
              <Tooltip key={c.category} title={`${c.strength} · ${c.confidence} confidence · ${c.rationale}`}>
                <Chip
                  size="small" label={c.category}
                  variant={c.strength === 'primary' ? 'filled' : 'outlined'}
                  color={CONFIDENCE_COLORS[c.confidence] || 'default'}
                />
              </Tooltip>
            ))}
          </Box>
        )}
      </TableCell>

      {/* Evidence: where this control's evidence actually comes from. Two
          states, not a pass/fail — a control with no rule is the normal case
          for most of a compliance framework, not a shortfall. */}
      <TableCell sx={{ verticalAlign: 'top', maxWidth: 340 }}>
        {enforced === null ? (
          <Tooltip title="We cannot say yet. Rules are written from what your agents report, which we learn from your scans.">
            <Chip size="small" icon={<HelpOutline />} label="After your first scan" variant="outlined" />
          </Tooltip>
        ) : enforced === 'none' ? (
          <>
            <Chip size="small" label="Category only" variant="outlined" />
            {control.unenforceable_reasons.map((reason, i) => (
              <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {reason}
              </Typography>
            ))}
          </>
        ) : (
          <>
            <Chip size="small" color="success" label="Rule" />
            {control.condition && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace', fontSize: '0.75rem' }}
              >
                {control.condition}
              </Typography>
            )}
          </>
        )}
      </TableCell>

      {/* The number the customer asked about: findings ARIAS has produced in
          this control's categories TODAY, before their framework existed. It
          is the reason a framework lights up on day one, and it is not a
          measure of this control's own rule. */}
      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
        <Tooltip
          title={
            categoryNames.length
              ? `${control.evidence_count} finding(s) ARIAS has already produced in ${categoryNames.join(', ')}, across every agent it has scanned. This control will collect them — it is not a count of anything this control's own rule found.`
              : 'No category, so this control collects nothing.'
          }
        >
          <Typography variant="body2" sx={{ cursor: 'help' }}>{control.evidence_count}</Typography>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
}

export default FrameworksPanel
